/**
 * App-wide UI font.
 *
 * Only the active family is loaded on startup (see the matching script in
 * index.html). The other families download when the Appearance tab opens so
 * their samples can render, and they stay off the critical path of every
 * other screen.
 *
 * Keep the boot script in index.html in sync with APP_FONTS. A personal choice
 * in nomadic_app_font wins. Otherwise the startup script uses the cached admin
 * default in nomadic_app_font_default, then the device system font.
 */

export const APP_FONT_STORAGE_KEY = "nomadic_app_font";
export const APP_FONT_DEFAULT_STORAGE_KEY = "nomadic_app_font_default";
export const DEFAULT_APP_FONT_ID = "system";

/** One stack for every platform. No file is downloaded. */
export const SYSTEM_FONT_STACK =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto';

export const APP_FONT_STACK =
  "var(--dm-font-sans), ui-sans-serif, system-ui, sans-serif";

export interface AppFontOption {
  id: string;
  label: string;
  /** CSS family name. Quoted when applied to --dm-font-sans. */
  family: string;
  /**
   * Full value for --dm-font-sans when the choice is a stack rather than one
   * downloaded family. Left unset for a single quoted family name.
   */
  cssValue?: string;
  description: string;
  /** null = no extra file. The default face keeps the Inter stylesheet. */
  href: string | null;
  /** Installed on the device. Turns the default Inter download off. */
  system?: boolean;
}

const WEIGHTS = "wght@400;500;600;700";

function googleFontHref(family: string): string {
  const encoded = family.trim().replace(/\s+/g, "+");
  return `https://fonts.googleapis.com/css2?family=${encoded}:${WEIGHTS}&display=swap`;
}

export const APP_FONTS: readonly AppFontOption[] = [
  {
    id: "inter",
    label: "Inter",
    family: "Inter",
    description: "Neutral and compact. A good fit for tables, events, and long sessions.",
    href: null,
  },
  {
    id: "system",
    label: "System (San Francisco on Mac and iPhone, Segoe UI on Windows, Roboto on Android)",
    family: "system-ui",
    cssValue: SYSTEM_FONT_STACK,
    description: "Nothing is downloaded. Each device uses the face already installed.",
    href: null,
    system: true,
  },
  {
    id: "space-grotesk",
    label: "Space Grotesk",
    family: "Space Grotesk",
    description: "Geometric brand face. Distinct headlines, a little wider in body text.",
    href: googleFontHref("Space Grotesk"),
  },
  {
    id: "manrope",
    label: "Manrope",
    family: "Manrope",
    description: "Geometric, but still quiet at small sizes. A good fit for dense review UI.",
    href: googleFontHref("Manrope"),
  },
  {
    id: "figtree",
    label: "Figtree",
    family: "Figtree",
    description: "Slightly warmer than Inter, with open letterforms that stay readable.",
    href: googleFontHref("Figtree"),
  },
  {
    id: "ibm-plex-sans",
    label: "IBM Plex Sans",
    family: "IBM Plex Sans",
    description: "Designed for interfaces and data. Clear at 12–13px in lists and metadata.",
    href: googleFontHref("IBM Plex Sans"),
  },
  {
    id: "plus-jakarta-sans",
    label: "Plus Jakarta Sans",
    family: "Plus Jakarta Sans",
    description: "Modern product sans with a bit more character than Inter.",
    href: googleFontHref("Plus Jakarta Sans"),
  },
];

const FONT_BY_ID = new Map(APP_FONTS.map((font) => [font.id, font]));

export function isAppFontId(value: unknown): value is string {
  return typeof value === "string" && FONT_BY_ID.has(value);
}

export function getAppFont(id: string | null | undefined): AppFontOption {
  if (id && FONT_BY_ID.has(id)) return FONT_BY_ID.get(id)!;
  return FONT_BY_ID.get(DEFAULT_APP_FONT_ID)!;
}

function readFontId(storageKey: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(storageKey);
    // Older builds saved this id for the same system stack.
    const canonical = stored === "san-francisco" ? "system" : stored;
    return isAppFontId(canonical) ? canonical : null;
  } catch {
    return null;
  }
}

/** A font this browser chose. Null when the admin default should apply. */
export function readPersonalAppFontId(): string | null {
  return readFontId(APP_FONT_STORAGE_KEY);
}

/** Last admin default this browser has seen. */
export function readCachedDefaultAppFontId(): string | null {
  return readFontId(APP_FONT_DEFAULT_STORAGE_KEY);
}

export function readStoredAppFontId(): string {
  return (
    readPersonalAppFontId() ??
    readCachedDefaultAppFontId() ??
    DEFAULT_APP_FONT_ID
  );
}

function ensureFontPreconnect() {
  if (document.getElementById("dm-font-preconnect")) return;
  const google = document.createElement("link");
  google.id = "dm-font-preconnect";
  google.rel = "preconnect";
  google.href = "https://fonts.googleapis.com";
  const files = document.createElement("link");
  files.rel = "preconnect";
  files.href = "https://fonts.gstatic.com";
  files.crossOrigin = "anonymous";
  document.head.appendChild(google);
  document.head.appendChild(files);
}

const INTER_STYLESHEET =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";

function setInterStylesheetEnabled(enabled: boolean) {
  let inter = document.getElementById("dm-font-inter") as HTMLLinkElement | null;
  if (!enabled) {
    if (inter) inter.media = "not all";
    return;
  }
  ensureFontPreconnect();
  if (!inter) {
    inter = document.createElement("link");
    inter.id = "dm-font-inter";
    inter.rel = "stylesheet";
    inter.href = INTER_STYLESHEET;
    document.head.appendChild(inter);
  }
  inter.media = "all";
}

function upsertFontStylesheet(id: string, href: string) {
  ensureFontPreconnect();
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
  return link;
}

/** Point the app at one family. Does not load the other choices. */
export function applyAppFont(id: string): AppFontOption {
  const font = getAppFont(id);
  if (typeof document === "undefined") return font;
  const root = document.documentElement;
  if (root.dataset.appFont === font.id && !font.href) {
    setInterStylesheetEnabled(!font.system);
    return font;
  }
  root.style.setProperty("--dm-font-sans", font.cssValue ?? `"${font.family}"`);
  root.dataset.appFont = font.id;

  setInterStylesheetEnabled(!font.href && !font.system);

  const active = document.getElementById("dm-app-font");
  if (!font.href) {
    active?.remove();
    return font;
  }

  const link = upsertFontStylesheet("dm-app-font", font.href);
  link.dataset.fontId = font.id;
  return font;
}

export function applyStoredAppFont(): AppFontOption {
  return applyAppFont(readStoredAppFontId());
}

/** Family list for a sample, so system stacks are not quoted as one name. */
export function appFontFamily(font: AppFontOption): string {
  if (font.cssValue) return `${font.cssValue}, ui-sans-serif, sans-serif`;
  return `"${font.family}", ui-sans-serif, system-ui, sans-serif`;
}

export function setAppFont(id: string): AppFontOption {
  const font = getAppFont(id);
  try {
    window.localStorage.setItem(APP_FONT_STORAGE_KEY, font.id);
  } catch {
    // The in-memory choice still applies for this page when storage is blocked.
  }
  return applyAppFont(font.id);
}

/** Drop the personal override so this browser follows the admin default again. */
export function clearPersonalAppFont(): AppFontOption {
  try {
    window.localStorage.removeItem(APP_FONT_STORAGE_KEY);
  } catch {
    // The applied default still takes effect for this page when storage is blocked.
  }
  return applyAppFont(readCachedDefaultAppFontId() ?? DEFAULT_APP_FONT_ID);
}

function rememberDefaultAppFontId(id: string): void {
  try {
    window.localStorage.setItem(APP_FONT_DEFAULT_STORAGE_KEY, getAppFont(id).id);
  } catch {
    // The in-memory default still applies for this page when storage is blocked.
  }
}

const APP_FONT_DEFAULT_CHECKED_KEY = "nomadic_app_font_default_checked_at";
const APP_FONT_DEFAULT_CHECK_TTL_MS = 30 * 60 * 1000;
let defaultRefreshGeneration = 0;
let defaultRefreshInFlight: Promise<string | null> | null = null;

function defaultCheckIsFresh(): boolean {
  if (!readCachedDefaultAppFontId()) return false;
  try {
    const checkedAt = Number(window.localStorage.getItem(APP_FONT_DEFAULT_CHECKED_KEY) || 0);
    return Number.isFinite(checkedAt) && Date.now() - checkedAt < APP_FONT_DEFAULT_CHECK_TTL_MS;
  } catch {
    return false;
  }
}

function rememberDefaultCheck(): void {
  try {
    window.localStorage.setItem(APP_FONT_DEFAULT_CHECKED_KEY, String(Date.now()));
  } catch {
    // A missed timestamp only means the next page load checks again.
  }
}

/**
 * Fetch the admin default without blocking paint. Skipped for 30 minutes
 * after a successful check so ordinary page loads do not call the server.
 * A response that started before an admin save is discarded.
 */
export function refreshAppFontDefault(): Promise<string | null> {
  if (defaultCheckIsFresh()) return Promise.resolve(readCachedDefaultAppFontId());
  if (defaultRefreshInFlight) return defaultRefreshInFlight;
  const generation = defaultRefreshGeneration;
  defaultRefreshInFlight = loadAppFontDefault(generation).finally(() => {
    defaultRefreshInFlight = null;
  });
  return defaultRefreshInFlight;
}

async function loadAppFontDefault(generation: number): Promise<string | null> {
  try {
    const { API_BASE_URL } = await import("@/config");
    const response = await fetch(`${API_BASE_URL}/app-font`);
    if (generation !== defaultRefreshGeneration) return readCachedDefaultAppFontId();
    if (!response.ok) return readCachedDefaultAppFontId();
    const payload = (await response.json()) as { font_id?: unknown };
    if (generation !== defaultRefreshGeneration) return readCachedDefaultAppFontId();
    if (!isAppFontId(payload.font_id)) return readCachedDefaultAppFontId();
    rememberDefaultAppFontId(payload.font_id);
    rememberDefaultCheck();
    if (!readPersonalAppFontId() && document.documentElement.dataset.appFont !== payload.font_id) {
      applyAppFont(payload.font_id);
    }
    return payload.font_id;
  } catch {
    return readCachedDefaultAppFontId();
  }
}

/** Run the default check after first paint, once the browser is idle. */
export function scheduleAppFontDefaultRefresh(): void {
  const run = () => {
    void refreshAppFontDefault();
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 4000 });
    return;
  }
  window.setTimeout(run, 1500);
}

export async function saveAdminAppFontDefault(
  id: string,
  token: string,
): Promise<AppFontOption> {
  const font = getAppFont(id);
  const { API_BASE_URL } = await import("@/config");
  const response = await fetch(`${API_BASE_URL}/app-font`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ font_id: font.id }),
  });
  if (!response.ok) {
    throw new Error("Failed to update the default font.");
  }
  defaultRefreshGeneration += 1;
  rememberDefaultAppFontId(font.id);
  rememberDefaultCheck();
  if (!readPersonalAppFontId()) applyAppFont(font.id);
  return font;
}

/**
 * Load every optional family for the picker samples. Non-render-blocking:
 * the stylesheet is applied only after it arrives, so opening Appearance
 * does not stall the profile page.
 */
export function preloadAppFontPreviews(): void {
  if (typeof document === "undefined") return;
  // Inter has no preview href because startup must not download it. Load it
  // here only, and only after the stylesheet arrives.
  preloadFontStylesheet("dm-font-preview-inter", INTER_STYLESHEET);
  for (const font of APP_FONTS) {
    if (!font.href) continue;
    preloadFontStylesheet(`dm-font-preview-${font.id}`, font.href);
  }
}

function preloadFontStylesheet(id: string, href: string): void {
  if (document.getElementById(id)) return;
  if (id === "dm-font-preview-inter" && document.getElementById("dm-font-inter")) return;
  ensureFontPreconnect();
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  link.media = "print";
  link.onload = () => {
    link.media = "all";
  };
  document.head.appendChild(link);
}
